/**
 * All sessions and Active sessions: the two views the Sessions page has
 * as tabs and the dashboard's Sessions widget has as a toggle (Warren,
 * 28 Oct 2026: "a widget that links to information from 'sessions'
 * switching between All sessions and Active sessions").
 *
 * The Sessions page keeps its tab in ?tab= (absent for All, `active` for
 * Active), so the widget's footer opens the same view it shows. Pure, so
 * the URL and toggle mapping are tested without a browser:
 * `tsx --test src/lib/sessionsViews.test.ts` from apps/admin/.
 */

export type SessionsView = 'all' | 'active';

export const SESSIONS_VIEWS: ReadonlyArray<{ id: SessionsView; label: string }> = [
  { id: 'all', label: 'All sessions' },
  { id: 'active', label: 'Active sessions' },
];

/** The Sessions page tab a URL asks for. Anything but ?tab=active is All. */
export function sessionsViewFromSearch(search: string): SessionsView {
  try {
    return new URLSearchParams(search).get('tab') === 'active' ? 'active' : 'all';
  } catch {
    return 'all';
  }
}

/** The search string for a tab, other params kept (All drops ?tab=). */
export function sessionsSearchForView(search: string, view: SessionsView): string {
  const params = new URLSearchParams(search);
  if (view === 'active') params.set('tab', 'active');
  else params.delete('tab');
  const out = params.toString();
  return out ? `?${out}` : '';
}

/** Where the Sessions page opens on that view. */
export function sessionsPath(view: SessionsView): string {
  return `/sessions${sessionsSearchForView('', view)}`;
}

/**
 * The widget's data per view, both from GET /dashboard/recent-sessions
 * (latest 10 in the caller's dashboard scope, newest start first). All
 * shares the Recent sessions widget's query and cache; Active adds
 * ?status=active. Both keys carry the viewer identity at position 1
 * (dashboardQueryIdentity, as every dashboard read does), so cached rows
 * never cross an account, tenant or persona change, and both sit under
 * ['dashboard', identity, 'recent-sessions'], which LIVE_QUERY_KEYS
 * refetches on every session event.
 */
export function recentSessionsQuery(
  view: SessionsView,
  identity: string,
): { queryKey: readonly string[]; status: 'active' | undefined } {
  return view === 'active'
    ? { queryKey: ['dashboard', identity, 'recent-sessions', 'active'], status: 'active' }
    : { queryKey: ['dashboard', identity, 'recent-sessions'], status: undefined };
}

/** The footer link: the Sessions page on the same view. `activeTotal` is
 *  the operator summary's count of sessions in progress, the number on
 *  the Active sessions tile. */
export function sessionsWidgetFooter(view: SessionsView, activeTotal?: number): { href: string; label: string } {
  if (view === 'all') return { href: sessionsPath('all'), label: 'View all sessions' };
  return {
    href: sessionsPath('active'),
    label: activeTotal && activeTotal > 0 ? `View all ${activeTotal} active` : 'View active sessions',
  };
}

export function sessionsWidgetEmptyCopy(view: SessionsView, isScoped: boolean): string {
  if (view === 'active') {
    return isScoped ? 'Nothing charging at your assigned locations right now.' : 'Nothing charging right now.';
  }
  return isScoped ? 'No sessions at your assigned locations yet.' : 'No sessions yet.';
}

/** "1h 12m" / "12m" / "under a minute": how long a session in progress has run. */
export function formatElapsed(startedAtIso: string, nowMs: number): string {
  const started = new Date(startedAtIso).getTime();
  if (!Number.isFinite(started)) return '';
  const minutes = Math.floor(Math.max(0, nowMs - started) / 60_000);
  if (minutes < 1) return 'under a minute';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** The widget remembers the last view per browser, a viewer convenience only. */
export const SESSIONS_WIDGET_VIEW_KEY = 'evi.admin.dashboard.sessions-view.v1';

/** With nothing remembered the widget opens on Active: All would repeat
 *  the Recent sessions widget beside it row for row. */
export const SESSIONS_WIDGET_DEFAULT_VIEW: SessionsView = 'active';

type ViewStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function readSessionsWidgetView(storage: ViewStorage | undefined): SessionsView {
  try {
    const stored = storage?.getItem(SESSIONS_WIDGET_VIEW_KEY);
    return stored === 'all' || stored === 'active' ? stored : SESSIONS_WIDGET_DEFAULT_VIEW;
  } catch {
    return SESSIONS_WIDGET_DEFAULT_VIEW;
  }
}

export function writeSessionsWidgetView(storage: ViewStorage | undefined, view: SessionsView): void {
  try {
    storage?.setItem(SESSIONS_WIDGET_VIEW_KEY, view);
  } catch {
    /* private mode or blocked storage: the toggle still works for this visit */
  }
}
